import {
  MdAttachMoney,
  MdBarChart,
  MdCheckCircle,
  MdChevronLeft,
  MdClose,
  MdDashboard,
  MdLogout,
  MdMenu,
  MdMenuBook,
  MdNotifications,
  MdPayment,
  MdPeople,
  MdPerson,
  MdSchool,
  MdSettings,
  MdSubscriptions,
  MdLocalLibrary,
  MdChat,
  MdHeadsetMic,
  MdAutoAwesome,
  MdViewList,
  MdExpandMore,
  MdHelpOutline,
} from 'react-icons/md';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { useAuth } from '../../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';

const Sidebar = ({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }) => {
  const { signOut, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  const isAnalyticsActive = location.pathname === '/analytics' || location.pathname === '/ai-analytics';
  const [analyticsExpanded, setAnalyticsExpanded] = useState(isAnalyticsActive);

  useEffect(() => {
    if (isAnalyticsActive) {
      setAnalyticsExpanded(true);
    }
  }, [location.pathname, isAnalyticsActive]);

  // Real-time listener for open support tickets
  useEffect(() => {
    if (userProfile?.role !== 'admin') return;
    const q = query(
      collection(db, 'support_messages'),
      where('status', '==', 'open')
    );
    const unsub = onSnapshot(q, (snap) => {
      setOpenTicketCount(snap.size);
    });
    return () => unsub();
  }, [userProfile?.role]);

  // Real-time listener for pending content approvals
  useEffect(() => {
    if (userProfile?.role !== 'admin' && userProfile?.role !== 'editor') return;
    const q = query(
      collection(db, 'content_approvals'),
      where('status', '==', 'pending')
    );
    const unsub = onSnapshot(q, (snap) => {
      if (location.pathname === '/content-approval') {
        localStorage.setItem('lastViewedContentApproval', Date.now().toString());
        setPendingApprovalCount(0);
        return;
      }
      
      const stored = parseInt(localStorage.getItem('lastViewedContentApproval') || '0', 10);
      const newItems = snap.docs.filter((doc) => {
        const data = doc.data();
        const createdAtTime = data.createdAt?.toDate?.()?.getTime() || (data.createdAt ? new Date(data.createdAt).getTime() : 0);
        return createdAtTime > stored;
      });
      setPendingApprovalCount(newItems.length);
    });
    return () => unsub();
  }, [userProfile?.role, location.pathname]);

  // Clear badge and update last viewed timestamp when navigating to Content Approval
  useEffect(() => {
    if (location.pathname === '/content-approval') {
      localStorage.setItem('lastViewedContentApproval', Date.now().toString());
      setPendingApprovalCount(0);
    }
  }, [location.pathname]);

  // Real-time listener for unread teacher chats
  useEffect(() => {
    if (userProfile?.role !== 'teacher') return;
    const q = query(
      collection(db, 'chats'),
      where('unreadByTeacher', '==', true)
    );
    const unsub = onSnapshot(q, (snap) => {
      setUnreadMessagesCount(snap.size);
    });
    return () => unsub();
  }, [userProfile?.role]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const closeMobileSidebar = () => {
    if (setIsMobileOpen) {
      setIsMobileOpen(false);
    }
  };

  const allMenuItems = [
    { path: '/', icon: MdDashboard, label: 'Dashboard', exact: true },
    { path: '/users', icon: MdPeople, label: 'Users' },
    { path: '/students', icon: MdSchool, label: 'Students' },
    { path: '/teachers', icon: MdPerson, label: 'Teachers' },
    { path: '/content-approval', icon: MdCheckCircle, label: 'Content Approval' },
    { path: '/subscriptions', icon: MdSubscriptions, label: 'Subscriptions' },
    { path: '/pricing', icon: MdAttachMoney, label: 'Pricing' },
    { path: '/payments', icon: MdPayment, label: 'Payments' },
    { 
      path: '/analytics', 
      icon: MdBarChart, 
      label: 'Analytics',
      subItems: [
        { path: '/analytics', label: 'Platform Overview', exact: true },
        { path: '/ai-analytics', label: 'AI Analytics', icon: MdAutoAwesome, badge: 'PRO' },
      ],
    },
    { path: '/library', icon: MdLocalLibrary, label: 'Library' },
    { path: '/notifications', icon: MdNotifications, label: 'Notifications' },
    { path: '/curriculum', icon: MdMenuBook, label: 'Curriculum' },
    { path: '/app-guide', icon: MdHelpOutline, label: 'How to Use App' },
    { path: '/settings', icon: MdSettings, label: 'Settings' },
    { path: '/support', icon: MdHeadsetMic, label: 'Support Inbox' },
    // Teacher Specific
    { path: '/', icon: MdDashboard, label: 'Overview', exact: true, forTeacher: true },
    { path: '/teacher/subjects', icon: MdMenuBook, label: 'My Subjects', forTeacher: true },
    { path: '/teacher/upload', icon: MdLocalLibrary, label: 'Upload Lesson', forTeacher: true },
    { path: '/teacher/quiz', icon: MdCheckCircle, label: 'Quiz Builder', forTeacher: true },
    { path: '/teacher/progress', icon: MdBarChart, label: 'Student Progress', forTeacher: true },
    { path: '/teacher/messages', icon: MdChat, label: 'Messages', forTeacher: true },
    { path: '/teacher/ai-assistant', icon: MdAutoAwesome, label: 'AI Assistant', forTeacher: true },
  ];

  let menuItems = allMenuItems;
  if (userProfile?.role === 'editor') {
    menuItems = allMenuItems.filter(item => ['/library', '/content-approval', '/curriculum', '/app-guide'].includes(item.path) && !item.forTeacher);
  } else if (userProfile?.role === 'teacher') {
    menuItems = allMenuItems.filter(item => item.forTeacher);
  } else {
    // Admin (or superadmin) - full access including teacher tools (excluding duplicate dashboard overview)
    menuItems = allMenuItems.filter(item => !item.forTeacher || item.path !== '/');
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <div 
        className={`h-screen bg-darkNavy flex-col fixed left-0 top-0 z-50 overflow-y-auto transition-all duration-300 hidden lg:flex ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {/* Logo Section with Toggle Button */}
        <div className="flex items-center justify-between px-5 py-6 border-b border-white/10">
          <div className="flex items-center gap-1 overflow-hidden">
            <img 
              src={logo} 
              alt="Smart Brain Logo" 
              className="w-10 h-10 flex-shrink-0"
            />
            {!isCollapsed && (
              <span className="text-xl font-bold text-white font-poppins whitespace-nowrap">
                Smart Brain
              </span>
            )}
          </div>
          
          <button
            onClick={toggleSidebar}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors flex-shrink-0"
            title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {isCollapsed ? <MdMenu className="text-lg" /> : <MdChevronLeft className="text-lg" />}
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isSupport = item.path === '/support';
            const isContentApproval = item.path === '/content-approval';
            
            const isTeacherMessages = item.path === '/teacher/messages';
            const badgeCount = isSupport 
              ? openTicketCount 
              : isContentApproval 
                ? pendingApprovalCount 
                : isTeacherMessages 
                  ? unreadMessagesCount 
                  : 0;
              
            const showBadge = badgeCount > 0;

            if (item.subItems) {
              const isGroupActive = isAnalyticsActive;
              return (
                <div key={item.path} className="space-y-1 group relative">
                  <button
                    type="button"
                    onClick={() => {
                      if (isCollapsed) {
                        navigate('/analytics');
                      } else {
                        setAnalyticsExpanded(!analyticsExpanded);
                        if (!isAnalyticsActive) {
                          navigate('/analytics');
                        }
                      }
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                      isGroupActive
                        ? 'bg-primary/20 text-white font-medium border border-primary/30 shadow-sm'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                    title={isCollapsed ? item.label : ''}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="relative flex-shrink-0">
                        <Icon className={`text-xl ${isGroupActive ? 'text-primary' : ''}`} />
                      </div>
                      {!isCollapsed && <span className="font-medium whitespace-nowrap">{item.label}</span>}
                    </div>
                    {!isCollapsed && (
                      <MdExpandMore
                        className={`text-lg text-white/50 transition-transform duration-200 ${
                          analyticsExpanded ? 'rotate-180 text-white' : ''
                        }`}
                      />
                    )}
                  </button>

                  {/* Desktop Submenu (Expanded Sidebar) */}
                  {!isCollapsed && analyticsExpanded && (
                    <div className="ml-5 pl-3 border-l border-white/15 space-y-1 py-1">
                      {item.subItems.map((sub) => {
                        const SubIcon = sub.icon;
                        return (
                          <NavLink
                            key={sub.path}
                            to={sub.path}
                            end={sub.exact}
                            className={({ isActive }) =>
                              `flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                isActive
                                  ? 'bg-primary text-white shadow-md shadow-primary/30 font-semibold'
                                  : 'text-white/60 hover:text-white hover:bg-white/10'
                              }`
                            }
                          >
                            <div className="flex items-center gap-2">
                              {SubIcon && <SubIcon className="text-sm flex-shrink-0 text-indigo-400" />}
                              <span>{sub.label}</span>
                            </div>
                            {sub.badge && (
                              <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                                {sub.badge}
                              </span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}

                  {/* Collapsed Sidebar Flyout Tooltip */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 top-0 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 shadow-xl border border-white/10 min-w-[170px]">
                      <div className="font-semibold text-white/90 pb-1.5 border-b border-white/10 mb-1.5 flex items-center gap-1.5">
                        <Icon className="text-primary text-base" />
                        <span>Analytics</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        {item.subItems.map((sub) => (
                          <NavLink
                            key={sub.path}
                            to={sub.path}
                            end={sub.exact}
                            className={({ isActive }) =>
                              `px-2.5 py-1.5 rounded-md text-xs transition-colors flex items-center justify-between gap-3 ${
                                isActive ? 'bg-primary text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                              }`
                            }
                          >
                            <span>{sub.label}</span>
                            {sub.badge && (
                              <span className="text-[9px] font-bold text-indigo-300">
                                {sub.badge}
                              </span>
                            )}
                          </NavLink>
                        ))}
                      </div>
                      <div className="absolute left-0 top-4 -translate-x-1 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45 border-l border-b border-white/10"></div>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-white/70 hover:bg-white/10 hover:text-white transition-all group relative ${
                    isActive ? 'bg-primary text-white shadow-lg shadow-primary/30' : ''
                  }`
                }
                title={isCollapsed ? item.label : ''}
              >
                <div className="relative flex-shrink-0">
                  <Icon className="text-xl" />
                  {showBadge && isCollapsed && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </div>
                {!isCollapsed && <span className="font-medium whitespace-nowrap flex-1">{item.label}</span>}
                {!isCollapsed && showBadge && (
                  <span className="ml-auto min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center animate-pulse">
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
                
                {isCollapsed && (
                  <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                    {item.label}{showBadge ? ` (${badgeCount})` : ''}
                    <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                  </div>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-white/10 space-y-3">
          <div className={`flex items-center gap-3 px-3 py-3 bg-white/5 rounded-xl ${isCollapsed ? 'justify-center' : ''}`}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
              {userProfile?.fullName?.charAt(0) || 'A'}
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{userProfile?.fullName || 'User'}</p>
                <p className="text-xs text-white/60 truncate">
                  {userProfile?.role === 'teacher' ? 'Teacher' : userProfile?.role === 'editor' ? 'Content Editor' : 'Super Administrator'}
                </p>
              </div>
            )}
          </div>

          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-4 py-3 bg-transparent border border-white/10 rounded-xl text-white/70 hover:bg-red-500/10 hover:border-red-500 hover:text-red-500 transition-all ${isCollapsed ? 'justify-center' : ''}`}
            title={isCollapsed ? 'Logout' : ''}
          >
            <MdLogout className="text-xl flex-shrink-0" />
            {!isCollapsed && <span className="font-medium whitespace-nowrap">Logout</span>}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div 
        className={`h-screen bg-darkNavy flex flex-col fixed left-0 top-0 z-50 overflow-y-auto transition-transform duration-300 w-64 lg:hidden ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-6 border-b border-white/10">
          <div className="flex items-center gap-1">
            <img src={logo} alt="Smart Brain Logo" className="w-10 h-10 flex-shrink-0" />
            <span className="text-xl font-bold text-white font-poppins">Smart Brain</span>
          </div>
          
          <button
            onClick={closeMobileSidebar}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <MdClose className="text-lg" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isSupport = item.path === '/support';
            const isContentApproval = item.path === '/content-approval';
            
            const isTeacherMessages = item.path === '/teacher/messages';
            const badgeCount = isSupport 
              ? openTicketCount 
              : isContentApproval 
                ? pendingApprovalCount 
                : isTeacherMessages 
                  ? unreadMessagesCount 
                  : 0;
              
            const showBadge = badgeCount > 0;

            if (item.subItems) {
              const isGroupActive = isAnalyticsActive;
              return (
                <div key={item.path} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => {
                      setAnalyticsExpanded(!analyticsExpanded);
                      if (!isAnalyticsActive) {
                        navigate('/analytics');
                      }
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                      isGroupActive
                        ? 'bg-primary/20 text-white font-medium border border-primary/30'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`text-xl ${isGroupActive ? 'text-primary' : ''}`} />
                      <span className="font-medium whitespace-nowrap">{item.label}</span>
                    </div>
                    <MdExpandMore
                      className={`text-lg text-white/50 transition-transform duration-200 ${
                        analyticsExpanded ? 'rotate-180 text-white' : ''
                      }`}
                    />
                  </button>

                  {analyticsExpanded && (
                    <div className="ml-5 pl-3 border-l border-white/15 space-y-1 py-1">
                      {item.subItems.map((sub) => {
                        const SubIcon = sub.icon;
                        return (
                          <NavLink
                            key={sub.path}
                            to={sub.path}
                            end={sub.exact}
                            onClick={closeMobileSidebar}
                            className={({ isActive }) =>
                              `flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                isActive
                                  ? 'bg-primary text-white shadow-md shadow-primary/30 font-semibold'
                                  : 'text-white/60 hover:text-white hover:bg-white/10'
                              }`
                            }
                          >
                            <div className="flex items-center gap-2">
                              {SubIcon && <SubIcon className="text-sm flex-shrink-0 text-indigo-400" />}
                              <span>{sub.label}</span>
                            </div>
                            {sub.badge && (
                              <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                                {sub.badge}
                              </span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                onClick={closeMobileSidebar}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-white/70 hover:bg-white/10 hover:text-white transition-all ${
                    isActive ? 'bg-primary text-white shadow-lg shadow-primary/30' : ''
                  }`
                }
              >
                <Icon className="text-xl flex-shrink-0" />
                <span className="font-medium whitespace-nowrap flex-1">{item.label}</span>
                {showBadge && (
                  <span className="min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center animate-pulse">
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-white/10 space-y-3">
          <div className="flex items-center gap-3 px-3 py-3 bg-white/5 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
              {userProfile?.fullName?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{userProfile?.fullName || 'User'}</p>
              <p className="text-xs text-white/60 truncate">
                {userProfile?.role === 'teacher' ? 'Teacher' : userProfile?.role === 'editor' ? 'Content Editor' : 'Super Administrator'}
              </p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 bg-transparent border border-white/10 rounded-xl text-white/70 hover:bg-red-500/10 hover:border-red-500 hover:text-red-500 transition-all"
          >
            <MdLogout className="text-xl flex-shrink-0" />
            <span className="font-medium whitespace-nowrap">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
